import React from 'react';
import { motion } from 'framer-motion';
import { FaLightbulb, FaHeart, FaRocket, FaUsers } from 'react-icons/fa';
import PublicNav from '../../components/PublicNav/PublicNav';
import PublicFooter from '../../components/PublicFooter/PublicFooter';
import './About.css';

const About = () => {
  const values = [
    {
      icon: <FaLightbulb />,
      title: 'Innovation',
      description: 'We constantly innovate to provide cutting-edge solutions for project management'
    },
    {
      icon: <FaHeart />,
      title: 'Customer First',
      description: 'Our customers are at the heart of everything we do'
    },
    {
      icon: <FaRocket />,
      title: 'Excellence',
      description: 'We strive for excellence in every aspect of our platform'
    },
    {
      icon: <FaUsers />,
      title: 'Collaboration',
      description: 'We believe in the power of teamwork and collaboration'
    }
  ];

  const team = [
    {
      name: 'John Doe',
      role: 'CEO & Founder',
      image: 'https://via.placeholder.com/150'
    },
    {
      name: 'Jane Smith',
      role: 'CTO',
      image: 'https://via.placeholder.com/150'
    },
    {
      name: 'Mike Johnson',
      role: 'Head of Product',
      image: 'https://via.placeholder.com/150'
    },
    {
      name: 'Sarah Williams',
      role: 'Head of Design',
      image: 'https://via.placeholder.com/150'
    }
  ];

  const milestones = [
    { year: '2020', event: 'Company Founded' },
    { year: '2021', event: '1,000+ Users' },
    { year: '2022', event: 'Series A Funding' },
    { year: '2023', event: '10,000+ Users' },
    { year: '2024', event: 'Global Expansion' }
  ];

  return (
    <div className="about-page">
      <PublicNav />
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="about-hero-content"
          >
            <h1 className="about-title">About Us</h1>
            <p className="about-subtitle">
              We're on a mission to revolutionize project management and help teams 
              achieve their goals more efficiently.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="story-section">
        <div className="container">
          <div className="story-grid">
            <motion.div
              className="story-content"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title">Our Story</h2>
              <p className="story-text">
                Founded in 2020, our platform was born from a simple idea: project management 
                should be intuitive, powerful, and accessible to everyone. We started as a small 
                team of passionate developers and designers who were frustrated with the complexity 
                of existing tools.
              </p>
              <p className="story-text">
                Today, we serve thousands of teams worldwide, from startups to enterprises, 
                helping them manage their projects more effectively. Our commitment to innovation 
                and customer satisfaction drives everything we do.
              </p>
            </motion.div>
            <motion.div
              className="story-image"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="image-placeholder">
                <FaRocket className="placeholder-icon" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Our Values</h2>
            <p className="section-subtitle">
              The principles that guide our work and decisions
            </p>
          </motion.div>

          <div className="values-grid">
            {values.map((value, index) => (
              <motion.div
                key={index}
                className="value-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="value-icon">{value.icon}</div>
                <h3 className="value-title">{value.title}</h3>
                <p className="value-description">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="timeline-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Our Journey</h2>
            <p className="section-subtitle">Key milestones in our growth</p>
          </motion.div>

          <div className="timeline">
            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                className="timeline-item"
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="timeline-content">
                  <div className="timeline-year">{milestone.year}</div>
                  <div className="timeline-event">{milestone.event}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="team-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Meet Our Team</h2>
            <p className="section-subtitle">
              The talented people behind our success
            </p>
          </motion.div>

          <div className="team-grid">
            {team.map((member, index) => (
              <motion.div
                key={index}
                className="team-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="team-image">
                  <img src={member.image} alt={member.name} />
                </div>
                <h3 className="team-name">{member.name}</h3>
                <p className="team-role">{member.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
};

export default About;


